import React, { useEffect, useState } from 'react';
import Skeleton from '../components/Skeleton.js';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useAuthStore } from '../stores/useAuthStore.js';
import { connectSocket, getSocket } from '../lib/socket.js';
import VideoPlayer from '../components/VideoPlayer.js';

export default function Highlights() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [clipsList, setClipsList] = useState<any[]>([]);
  const [selectedGamemode, setSelectedGamemode] = useState('all');
  const [selectedSort, setSelectedSort] = useState('recent');
  const [isLoading, setIsLoading] = useState(true);

  // Active clip state for modal player
  const [activeModalClip, setActiveModalClip] = useState<any>(null);

  // Load clips feed
  const fetchClips = async () => {
    try {
      const gmFilter = selectedGamemode !== 'all' ? `&gamemode=${selectedGamemode}` : '';
      const response = await api.get(`/api/clips?sort=${selectedSort}${gmFilter}`);
      setClipsList(response.data || []);
    } catch (err) {
      console.error('Failed to load highlights feed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchClips();
  }, [selectedGamemode, selectedSort]);

  // Connect socket and register live updates for clip likes
  useEffect(() => {
    connectSocket();
    const socket = getSocket();

    const handleClipLiked = (data: { clipId: string; likeCount: number }) => {
      setClipsList((prev) =>
        prev.map((clip) => {
          if (clip.id === data.clipId) {
            return {
              ...clip,
              likeCount: data.likeCount
            };
          }
          return clip;
        })
      );

      // If active modal clip is being viewed, update it too
      setActiveModalClip((prev: any) => {
        if (prev && prev.id === data.clipId) {
          return { ...prev, likeCount: data.likeCount };
        }
        return prev;
      });
    };

    socket.on('clip:liked', handleClipLiked);

    return () => {
      socket.off('clip:liked', handleClipLiked);
    };
  }, []);

  const handleLikeToggle = async (clipId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Avoid opening player modal
    // Optimistic update: toggle immediately and animate
    setClipsList((prev) =>
      prev.map((clip) => {
        if (clip.id === clipId) {
          const willLike = !clip.liked_by_me;
          return {
            ...clip,
            liked_by_me: willLike,
            likeCount: clip.likeCount + (willLike ? 1 : -1),
            _isLiking: true
          };
        }
        return clip;
      })
    );

    setActiveModalClip((prev: any) => (prev && prev.id === clipId ? { ...prev, liked_by_me: !prev.liked_by_me, likeCount: prev.likeCount + (prev.liked_by_me ? -1 : 1) } : prev));

    // Clear animation flag after animation duration
    setTimeout(() => {
      setClipsList((prev) => prev.map((c) => (c.id === clipId ? { ...c, _isLiking: false } : c)));
    }, 150);

    try {
      const response = await api.post(`/api/clips/${clipId}/like`);
      const { liked, like_count } = response.data;

      // Reconcile with server response
      setClipsList((prev) =>
        prev.map((clip) => (clip.id === clipId ? { ...clip, liked_by_me: liked, likeCount: like_count } : clip))
      );

      if (activeModalClip && activeModalClip.id === clipId) {
        setActiveModalClip((prev: any) => ({ ...prev, liked_by_me: liked, likeCount: like_count }));
      }
    } catch (err) {
      // Revert optimistic update on error
      setClipsList((prev) =>
        prev.map((clip) => {
          if (clip.id === clipId) {
            // flip back
            const revertedLiked = !clip.liked_by_me;
            return { ...clip, liked_by_me: revertedLiked, likeCount: Math.max(0, clip.likeCount + (revertedLiked ? 1 : -1)) };
          }
          return clip;
        })
      );
      console.error('Failed to toggle like:', err);
    }
  };

  const handleOpenClip = async (clip: any) => {
    setActiveModalClip(clip);
    // Hit GET /api/clips/:id silently to increment view count on backend
    try {
      const response = await api.get(`/api/clips/${clip.id}`);
      // Update local view count
      setClipsList((prev) =>
        prev.map((c) => (c.id === clip.id ? { ...c, viewCount: response.data.viewCount } : c))
      );
    } catch (err) {
      console.error('Failed to log clip view:', err);
    }
  };

  const getGamemodeColor = (gm: string) => {
    switch (gm) {
      case 'mace':
      case 'mace_rockets':
        return 'bg-[rgba(255,191,0,0.15)] text-[#ffbf00]';
      case 'sword':
      case 'cpvp':
        return 'bg-[rgba(224,60,60,0.15)] text-[#e03c3c]';
      case 'axe_shield':
        return 'bg-[rgba(76,175,80,0.15)] text-[#4caf50]';
      default:
        return 'bg-[rgba(255,255,255,0.08)] text-on-surface-variant';
    }
  };

  if (isLoading) {
    return (
      <div className="flex-1 min-h-screen bg-background flex items-center justify-center text-on-surface-variant font-medium">
        <div className="w-full max-w-6xl px-gutter">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-stack-md">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-video rounded-lg overflow-hidden">
                <Skeleton />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-screen bg-background text-on-surface select-none">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-headline-lg text-headline-lg text-on-surface mb-2">Community Highlights</h1>
          <p className="text-on-surface-variant text-body-sm">
            Watch training showcase clips and share your own high-performance clips.
          </p>
        </div>
        <button
          onClick={() => navigate('/upload')}
          className="bg-error hover:brightness-110 active:scale-95 text-white px-5 py-2.5 rounded-[4px] font-button text-button uppercase tracking-wider font-bold transition-all flex items-center gap-2"
        >
          Upload Clip
          <span className="material-symbols-outlined text-[18px]">upload</span>
        </button>
      </div>

      {/* Filters & Sorting */}
      <div className="flex flex-wrap gap-4 items-center bg-[#141414] thin-border p-4 rounded-lg mb-8">
        {/* Gamemode filter */}
        <div className="flex flex-col gap-1 min-w-[150px]">
          <label className="text-[10px] uppercase tracking-wider text-on-surface-variant font-bold">
            Gamemode Filter
          </label>
          <select
            value={selectedGamemode}
            onChange={(e) => setSelectedGamemode(e.target.value)}
            className="bg-surface-container border border-white/10 rounded px-2.5 py-1.5 text-on-surface text-[13px] outline-none focus:border-error focus:ring-0"
          >
            <option value="all">All Modes</option>
            <option value="sword">Sword</option>
            <option value="mace">Mace</option>
            <option value="mace_rockets">Mace Rockets</option>
            <option value="cpvp">Crystal PvP</option>
            <option value="axe_shield">Axe & Shield</option>
            <option value="nethpot">Nethpot</option>
            <option value="tank">Tank</option>
          </select>
        </div>

        {/* Sorting selector */}
        <div className="flex flex-col gap-1 min-w-[150px]">
          <label className="text-[10px] uppercase tracking-wider text-on-surface-variant font-bold">
            Sort By
          </label>
          <select
            value={selectedSort}
            onChange={(e) => setSelectedSort(e.target.value)}
            className="bg-surface-container border border-white/10 rounded px-2.5 py-1.5 text-on-surface text-[13px] outline-none focus:border-error focus:ring-0"
          >
            <option value="recent">Recent Uploads</option>
            <option value="liked">Most Liked</option>
            <option value="trending">Trending (Active)</option>
          </select>
        </div>
      </div>

      {/* Clips Grid */}
      {clipsList.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-stack-md">
          {clipsList.map((clip, idx) => (
            <div
              key={clip.id}
              onClick={() => handleOpenClip(clip)}
              className={`bg-[#141414] thin-border rounded-lg overflow-hidden group hover:border-white/20 transition-all cursor-pointer flex flex-col justify-between fade-in-card`}
              style={{ animationDelay: `${idx * 50}ms` }}
            >
              <div className="aspect-video relative overflow-hidden bg-black">
                <img
                  alt={clip.title}
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                  src={clip.thumbnailUrl}
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                  <span className="material-symbols-outlined text-white text-4xl">play_circle</span>
                </div>
                <div className="absolute top-3 left-3">
                  <span
                    className={`px-2 py-0.5 rounded-[4px] text-[10px] font-bold uppercase ${getGamemodeColor(
                      clip.gamemode
                    )}`}
                  >
                    {clip.gamemode}
                  </span>
                </div>
              </div>

              <div className="p-3 space-y-1">
                <h4 className="font-semibold text-on-surface text-body-sm truncate">{clip.title}</h4>
                <div className="flex justify-between items-center text-[11px] text-on-surface-variant">
                  <span>@{clip.user?.username || 'user'}</span>
                  <span>{clip.viewCount} views</span>
                </div>
              </div>

              {/* Likes trigger */}
              <div className="px-3 pb-3 pt-1 border-t border-white/5 flex justify-between items-center">
                <button
                  type="button"
                  onClick={(e) => handleLikeToggle(clip.id, e)}
                  className={`flex items-center gap-1.5 text-[12px] font-semibold ${
                    clip.liked_by_me ? 'text-error' : 'text-on-surface-variant hover:text-error'
                  }`}
                >
                  <span
                    className={`material-symbols-outlined text-[18px] ${clip._isLiking ? 'like-pop' : ''}`}
                    style={{ fontVariationSettings: clip.liked_by_me ? "'FILL' 1" : "'FILL' 0" }}
                  >
                    favorite
                  </span>
                  <span>{clip.likeCount}</span>
                </button>
                <span className="text-[10px] font-mono text-outline">
                  {new Date(clip.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          ))}
      </div>
      ) : (
        <div className="bg-[#141414] thin-border rounded-lg p-stack-lg text-center text-on-surface-variant">
          No clips match your filter. Upload the first high-performance clip!
        </div>
      )}

      {/* Video Overlay Modal (highlights_feed_pvp_academy_2) */}
      {activeModalClip && (
        <div
          onClick={() => setActiveModalClip(null)}
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 animate-fade-in cursor-default"
        >
          <div
            onClick={(e) => e.stopPropagation()} // Stop closing
            className="w-full max-w-4xl bg-[#0d0d0d] thin-border rounded-lg overflow-hidden flex flex-col"
          >
            <div className="p-4 border-b border-outline-variant flex justify-between items-center bg-surface-container select-none">
              <div>
                <h3 className="font-headline-sm text-body-lg text-on-surface truncate pr-4">
                  {activeModalClip.title}
                </h3>
                <span className="text-[11px] text-on-surface-variant">
                  by @{activeModalClip.user?.username || 'user'} • {activeModalClip.viewCount} views
                </span>
              </div>
              <button
                type="button"
                onClick={() => setActiveModalClip(null)}
                className="text-on-surface-variant hover:text-on-surface"
              >
                <span className="material-symbols-outlined text-[24px]">close</span>
              </button>
            </div>

            {/* Custom video player in modal */}
            <div className="p-4 bg-black">
              <VideoPlayer
                src={activeModalClip.videoUrl}
                thumbnailUrl={activeModalClip.thumbnailUrl}
              />
            </div>

            {/* Info details */}
            <div className="p-gutter flex justify-between items-start gap-4">
              <div>
                <span
                  className={`px-2 py-0.5 rounded-[4px] text-[10px] font-bold uppercase mb-2 inline-block ${getGamemodeColor(
                    activeModalClip.gamemode
                  )}`}
                >
                  {activeModalClip.gamemode}
                </span>
                <p className="text-on-surface-variant text-body-sm mt-1 leading-relaxed">
                  {activeModalClip.description || 'No description provided.'}
                </p>
              </div>

              <button
                type="button"
                onClick={(e) => handleLikeToggle(activeModalClip.id, e)}
                className={`px-5 py-2.5 rounded-[4px] border flex items-center gap-2 font-button text-button uppercase tracking-wider font-bold transition-all ${
                  activeModalClip.liked_by_me
                    ? 'border-error/20 bg-error/10 text-error'
                    : 'border-white/10 hover:bg-white/5 text-on-surface'
                }`}
              >
                <span
                  className="material-symbols-outlined text-[18px]"
                  style={{ fontVariationSettings: activeModalClip.liked_by_me ? "'FILL' 1" : "'FILL' 0" }}
                >
                  favorite
                </span>
                <span>{activeModalClip.likeCount} Likes</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
